import React, { useCallback, useEffect, useState } from "react";
import {
  Alert, alpha, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, Paper, Radio, Snackbar,
  Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography, Zoom,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SimCardIcon from "@mui/icons-material/SimCard";
import RouterIcon from "@mui/icons-material/Router";
import LinkIcon from "@mui/icons-material/Link";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import UndoIcon from "@mui/icons-material/Undo";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import axios from "../../../config/axiosConfig";

// OT "Adición de Chips" (solo AVL/Scada): se elige un módem del cliente y uno o más chips Inactivos de tu sucursal; el chip solo se actualiza
// y se registra en el momento de emparejarlo (queda Asignado, relacionado a ese módem, y pasa a Activo al finalizar la OT). Un emparejamiento de esta OT se puede deshacer mientras
// la OT no esté finalizada; "Finalizar" cierra la OT. Las tres tablas se paginan en el servidor.
const headCellSx = {
  fontWeight: 800,
  bgcolor: (t) => (t.palette.mode === "dark" ? t.palette.background.paper : t.palette.grey[100]),
};
const tableSx = { "& .MuiTableCell-root": { py: 0.25, px: 0.6, fontSize: 11.5 } };
const OPCIONES_FILAS = [10, 25, 50, 100];

const mensajeError = (e, defecto) => e?.response?.data?.error || e?.message || defecto;

// Paginador compartido por las tres tablas (MUI cuenta las páginas desde 0; la API desde 1).
function Paginador({ total, page, limit, onPage, onLimit }) {
  if (!total) return null;
  return (
    <TablePagination
      component="div" count={total} page={page} rowsPerPage={limit} rowsPerPageOptions={OPCIONES_FILAS}
      onPageChange={(_, p) => onPage(p)} onRowsPerPageChange={(e) => onLimit(Number(e.target.value))}
      labelRowsPerPage="Filas" labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
      sx={{ "& .MuiTablePagination-toolbar": { minHeight: 36 }, "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { fontSize: 12, m: 0 } }}
    />
  );
}

// Cabecera de card: icono en un círculo tintado del color de la card, título y contador a la derecha del título.
function CabeceraCard({ icono: Icono, color, titulo, total, children }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, flexWrap: "wrap", rowGap: 0.75 }}>
      <Box sx={{ width: 30, height: 30, borderRadius: "50%", display: "grid", placeItems: "center", color: `${color}.main`, bgcolor: (t) => alpha(t.palette[color].main, 0.14) }}>
        <Icono sx={{ fontSize: 18 }} />
      </Box>
      <Typography sx={{ fontWeight: 800 }}>{titulo}</Typography>
      {total !== undefined && <Chip size="small" color={color} label={total} sx={{ fontWeight: 800, height: 20 }} />}
      <Box sx={{ flex: 1 }} />
      {children}
    </Stack>
  );
}

const vacio = { modems: [], chips: [], emparejados: [], modems_total: 0, chips_total: 0, emparejados_total: 0, ciudad: null, estado_ot: 1 };

// El botón "Finalizar OT" vive en la barra superior de la pantalla: avisa el total de emparejados con onEmparejados y pide abrir el diálogo
// de finalización incrementando solicitudFinalizar.
export default function AdicionChipsOT({ cod_ot, onFinalizado, onEmparejados, solicitudFinalizar = 0 }) {
  const [datos, setDatos] = useState(vacio);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [modem, setModem] = useState("");
  const [seleccion, setSeleccion] = useState([]); // se conserva al cambiar de página
  const [observacion, setObservacion] = useState("");
  const [trabajando, setTrabajando] = useState(false);
  const [confirmar, setConfirmar] = useState(null); // "emparejar" | "finalizar" | { deshacer: numero }
  const [toast, setToast] = useState("");

  // búsquedas (con espera) y páginas/tamaños de cada tabla
  const [buscarChip, setBuscarChip] = useState("");
  const [buscarModem, setBuscarModem] = useState("");
  const [qChip, setQChip] = useState("");
  const [qModem, setQModem] = useState("");
  const [pgChips, setPgChips] = useState(0);
  const [limChips, setLimChips] = useState(25);
  const [pgModems, setPgModems] = useState(0);
  const [limModems, setLimModems] = useState(10);
  const [pgEmp, setPgEmp] = useState(0);
  const [limEmp, setLimEmp] = useState(10);

  useEffect(() => {
    const t = setTimeout(() => { setQChip(buscarChip.trim()); setPgChips(0); }, 300);
    return () => clearTimeout(t);
  }, [buscarChip]);
  useEffect(() => {
    const t = setTimeout(() => { setQModem(buscarModem.trim()); setPgModems(0); }, 300);
    return () => clearTimeout(t);
  }, [buscarModem]);

  const cargar = useCallback(async () => {
    try {
      const { data } = await axios.get(`/api/ot-adicion-chips/disponibles/${cod_ot}`, {
        params: {
          q: qChip || undefined, q_modem: qModem || undefined,
          chips_page: pgChips + 1, chips_limit: limChips,
          modems_page: pgModems + 1, modems_limit: limModems,
          emp_page: pgEmp + 1, emp_limit: limEmp,
        },
      });
      setDatos(data);
      setError("");
    } catch (e) {
      setError(mensajeError(e, "No se pudieron cargar los módems y chips."));
    } finally {
      setCargando(false);
    }
  }, [cod_ot, qChip, qModem, pgChips, limChips, pgModems, limModems, pgEmp, limEmp]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => { onEmparejados?.(datos.emparejados_total); }, [datos.emparejados_total, onEmparejados]);

  useEffect(() => { if (solicitudFinalizar > 0) setConfirmar("finalizar"); }, [solicitudFinalizar]);

  // Si al emparejar/deshacer una página queda vacía (era la última), se vuelve a la última página con datos.
  useEffect(() => {
    const ultima = (total, limite) => Math.max(Math.ceil(total / limite) - 1, 0);
    if (!datos.chips.length && datos.chips_total > 0 && pgChips > ultima(datos.chips_total, limChips)) setPgChips(ultima(datos.chips_total, limChips));
    if (!datos.modems.length && datos.modems_total > 0 && pgModems > ultima(datos.modems_total, limModems)) setPgModems(ultima(datos.modems_total, limModems));
    if (!datos.emparejados.length && datos.emparejados_total > 0 && pgEmp > ultima(datos.emparejados_total, limEmp)) setPgEmp(ultima(datos.emparejados_total, limEmp));
  }, [datos, pgChips, pgModems, pgEmp, limChips, limModems, limEmp]);

  const alternar = (numero) => setSeleccion((prev) => (prev.includes(numero) ? prev.filter((x) => x !== numero) : [...prev, numero]));
  const modemElegido = datos.modems.find((m) => m.num_sim === modem);

  const ejecutar = async () => {
    const accion = confirmar;
    setConfirmar(null);
    setTrabajando(true);
    setError("");
    try {
      if (accion === "emparejar") {
        await axios.post(`/api/ot-adicion-chips/emparejar/${cod_ot}`, { modem, chips: seleccion });
        setToast(`${seleccion.length} chip(s) emparejado(s) con el módem ${modem}.`);
        setSeleccion([]);
        await cargar();
      } else if (accion?.deshacer) {
        await axios.post(`/api/ot-adicion-chips/deshacer/${cod_ot}`, { chip: accion.deshacer });
        setToast(`Se deshizo el emparejamiento del chip ${accion.deshacer}.`);
        await cargar();
      } else if (accion === "finalizar") {
        const { data } = await axios.patch(`/api/ot-adicion-chips/finalizar/${cod_ot}`, { observacion: observacion.trim() });
        onFinalizado?.(data);
      }
    } catch (e) {
      setError(mensajeError(e, "No se pudo completar la operación."));
    } finally {
      setTrabajando(false);
    }
  };

  if (cargando) return <Box sx={{ p: 3, display: "grid", placeItems: "center" }}><CircularProgress size={26} /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 1.25 }} onClose={() => setError("")}>{error}</Alert>}
      <Alert severity="info" sx={{ mb: 1.25, py: 0, fontSize: 12.5 }}>
        Elige los chips Inactivos{datos.ciudad ? ` de ${datos.ciudad}` : ""} y el módem del cliente al que quedarán relacionados. Los chips solo cambian
        (pasan a Asignado) cuando los empareja y se activan al finalizar la OT; hasta entonces no se modifica nada.
      </Alert>

      <Paper variant="outlined" sx={{ p: 1.25, mb: 1.5, borderColor: (t) => alpha(t.palette.success.main, 0.45) }}>
        <CabeceraCard icono={LinkIcon} color="success" titulo="Chips emparejados en esta OT" total={datos.emparejados_total} />
        <TableContainer>
          <Table size="small" sx={tableSx}>
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>Chip</TableCell>
                <TableCell sx={headCellSx}>ICC</TableCell>
                <TableCell sx={headCellSx}>Modelo</TableCell>
                <TableCell sx={headCellSx}>Módem</TableCell>
                <TableCell sx={headCellSx} align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {!datos.emparejados.length && (
                <TableRow><TableCell colSpan={5}><Typography variant="body2" color="text.secondary">Todavía no emparejaste ningún chip en esta OT.</Typography></TableCell></TableRow>
              )}
              {datos.emparejados.map((e) => (
                <TableRow key={e.id_historial}>
                  <TableCell sx={{ fontWeight: 700 }}>{e.numero}</TableCell>
                  <TableCell>{e.emai}</TableCell>
                  <TableCell>{e.modelo}</TableCell>
                  <TableCell>{e.relacion}</TableCell>
                  <TableCell align="right">
                    <Button size="small" color="error" startIcon={<UndoIcon sx={{ fontSize: 14 }} />} sx={{ py: 0, fontSize: 11 }} disabled={trabajando} onClick={() => setConfirmar({ deshacer: e.numero })}>Deshacer</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Paginador total={datos.emparejados_total} page={pgEmp} limit={limEmp} onPage={setPgEmp} onLimit={(l) => { setLimEmp(l); setPgEmp(0); }} />
      </Paper>

      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
        <Paper variant="outlined" sx={{ p: 1.25, flex: 1.3, minWidth: 0 }}>
          <CabeceraCard icono={SimCardIcon} color="primary" titulo={`1. Chips Inactivos${datos.ciudad ? ` · ${datos.ciudad}` : ""}`} total={datos.chips_total}>
            <TextField
              size="small" placeholder="Buscar número, ICC o modelo…" value={buscarChip} onChange={(e) => setBuscarChip(e.target.value)} sx={{ width: 270 }}
              slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
            />
          </CabeceraCard>
          <TableContainer>
            <Table size="small" sx={tableSx}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headCellSx, width: 34 }}>Sel.</TableCell>
                  <TableCell sx={headCellSx}>Número</TableCell>
                  <TableCell sx={headCellSx}>ICC</TableCell>
                  <TableCell sx={headCellSx}>Modelo</TableCell>
                  <TableCell sx={headCellSx}>Ciudad</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!datos.chips.length && (
                  <TableRow><TableCell colSpan={5}><Typography variant="body2" color="text.secondary">{qChip ? "Ningún chip Inactivo coincide." : "No hay chips Inactivos disponibles."}</Typography></TableCell></TableRow>
                )}
                {datos.chips.map((c) => (
                  <TableRow key={c.numero} hover selected={seleccion.includes(c.numero)} onClick={() => alternar(c.numero)} sx={{ cursor: "pointer" }}>
                    <TableCell><Checkbox size="small" checked={seleccion.includes(c.numero)} onClick={(e) => e.stopPropagation()} onChange={() => alternar(c.numero)} sx={{ p: 0 }} /></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{c.numero}</TableCell>
                    <TableCell>{c.emai}</TableCell>
                    <TableCell>{c.modelo}</TableCell>
                    <TableCell>{c.ciudad}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Paginador total={datos.chips_total} page={pgChips} limit={limChips} onPage={setPgChips} onLimit={(l) => { setLimChips(l); setPgChips(0); }} />
        </Paper>

        <Paper variant="outlined" sx={{ p: 1.25, flex: 1, minWidth: 0 }}>
          <CabeceraCard icono={RouterIcon} color="info" titulo="2. Módem del cliente" total={datos.modems_total}>
            <TextField
              size="small" placeholder="Buscar módem…" value={buscarModem} onChange={(e) => setBuscarModem(e.target.value)} sx={{ width: 190 }}
              slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
            />
          </CabeceraCard>
          <TableContainer>
            <Table size="small" sx={tableSx}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headCellSx, width: 34 }}>Sel.</TableCell>
                  <TableCell sx={headCellSx}>Num. SIM</TableCell>
                  <TableCell sx={headCellSx}>ID</TableCell>
                  <TableCell sx={headCellSx}>Modelo</TableCell>
                  <TableCell sx={headCellSx}>Estado</TableCell>
                  <TableCell sx={{ ...headCellSx, textAlign: "center" }}>Chips</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!datos.modems.length && (
                  <TableRow><TableCell colSpan={6}><Typography variant="body2" color="text.secondary">{qModem ? "Ningún módem coincide." : "Este cliente no tiene módems Activos ni Vendidos Activos."}</Typography></TableCell></TableRow>
                )}
                {datos.modems.map((m) => (
                  <TableRow key={m.num_sim} hover selected={modem === m.num_sim} onClick={() => setModem(m.num_sim)} sx={{ cursor: "pointer" }}>
                    <TableCell><Radio size="small" checked={modem === m.num_sim} onClick={(e) => e.stopPropagation()} onChange={() => setModem(m.num_sim)} sx={{ p: 0 }} /></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{m.num_sim}</TableCell>
                    <TableCell>{m.id}</TableCell>
                    <TableCell>{m.modelo}</TableCell>
                    <TableCell>{m.estado}</TableCell>
                    <TableCell align="center">{m.chips_relacionados}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Paginador total={datos.modems_total} page={pgModems} limit={limModems} onPage={setPgModems} onLimit={(l) => { setLimModems(l); setPgModems(0); }} />
        </Paper>
      </Stack>

      {/* Barra flotante: aparece solo con un módem y al menos un chip elegidos */}
      <Zoom in={!!modem && seleccion.length > 0 && !confirmar}>
        <Paper
          elevation={8}
          sx={{ position: "fixed", right: { xs: 12, sm: 28 }, bottom: { xs: 12, sm: 24 }, zIndex: (t) => t.zIndex.snackbar - 1, p: 1, pl: 1.5, borderRadius: 3, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", maxWidth: "calc(100vw - 24px)" }}
        >
          <Chip size="small" color="primary" icon={<SimCardIcon />} label={`${seleccion.length} chip(s)`} sx={{ fontWeight: 700 }} />
          <Typography component="span" sx={{ color: "text.secondary" }}>→</Typography>
          <Chip size="small" color="info" icon={<RouterIcon />} label={modem} sx={{ fontWeight: 700 }} />
          <Button variant="contained" color="success" startIcon={<LinkIcon />} disabled={trabajando} onClick={() => setConfirmar("emparejar")} sx={{ fontWeight: 800 }}>
            Emparejar seleccionados
          </Button>
        </Paper>
      </Zoom>

      <Dialog open={!!confirmar} onClose={() => setConfirmar(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 800 }}>
          {confirmar === "finalizar" && <CheckCircleIcon color="success" sx={{ verticalAlign: "middle", mr: 0.75 }} />}
          {confirmar === "emparejar" && <WarningAmberIcon color="warning" sx={{ verticalAlign: "middle", mr: 0.75 }} />}
          {confirmar?.deshacer && <UndoIcon color="error" sx={{ verticalAlign: "middle", mr: 0.75 }} />}
          {confirmar === "emparejar" ? "¿Confirmar el emparejamiento?" : confirmar === "finalizar" ? "Finalizar OT" : "Deshacer emparejamiento"}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>
            {confirmar === "finalizar" && `¿Finalizar la OT con ${datos.emparejados_total} chip(s) emparejado(s)? Después ya no se podrán deshacer.`}
            {confirmar?.deshacer && `¿Deshacer el emparejamiento del chip ${confirmar.deshacer}? Volverá a Inactivo.`}
          </Typography>
          {confirmar === "emparejar" && (
            <>
              <Typography sx={{ fontSize: 14, mb: 1.25 }}>
                Vas a emparejar <b>{seleccion.length} chip(s)</b> con el módem <b>{modem}</b>{modemElegido?.modelo ? ` (${modemElegido.modelo})` : ""}. Los chips pasarán a
                <b> Asignado</b> y se registrarán en su historial en este momento; pasarán a <b>Activo</b> cuando finalices la OT. Podrás deshacerlo mientras la OT no esté finalizada.
              </Typography>
              <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5, maxHeight: 120, overflowY: "auto" }}>
                {seleccion.map((n) => <Chip key={n} size="small" color="primary" variant="outlined" icon={<SimCardIcon />} label={n} />)}
              </Stack>
            </>
          )}
          {confirmar === "finalizar" && (
            <TextField size="small" fullWidth multiline minRows={2} label="Observación (opcional)" value={observacion} onChange={(e) => setObservacion(e.target.value)} slotProps={{ htmlInput: { maxLength: 250 } }} sx={{ mt: 1.5 }} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmar(null)}>Cancelar</Button>
          <Button variant="contained" autoFocus color={confirmar?.deshacer ? "error" : confirmar === "emparejar" ? "success" : "primary"} onClick={ejecutar}>{confirmar === "emparejar" ? "Sí, emparejar" : "Confirmar"}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast("")} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity="success" variant="filled" onClose={() => setToast("")} sx={{ fontWeight: 600 }}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
}
